import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  format,
  setMilliseconds,
  startOfDay,
} from "date-fns";
import {
  ArchiveIcon,
  ArrowRightCircleIcon,
  CanvasIcon,
  CheckIcon,
  CommentIcon,
  DeleteIcon,
  EditIcon,
  EyeIcon,
  HistoryIcon,
  LightningBoltIcon,
  LinkIcon,
  PaperclipIcon,
  PlusIcon,
  TagIcon,
  UserIcon,
} from "@hightouch/htui";
import type { ActivityEvent } from "@hightouch/lib/inbox/activity";
import {
  AMP_BOARD_COLOR_NAMES,
  AMP_BOARD_COLORS,
  AMP_PROJECT_EXTERNAL_REF_TYPES,
  type AmpBoardColorName,
  type AmpProjectExternalRef,
  type AmpProjectExternalRefType,
} from "@hightouch/lib/ad-studio/board/constants";
import { z } from "zod";

import type {
  AmpProjectRowFragment,
  AmpTaskRowFragment,
  AmpWorkflowStatusFragment,
  CreateAmpTaskInput,
} from "src/graphql";
import {
  libraryFilePath,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";
import { formatRelativeShort } from "src/utils/time";

// Mirrors AMP_WORKFLOW_STATUS_CATEGORIES in packages/backend/lib/ad-studio/board/constants.ts.
export const STATUS_CATEGORIES = [
  "backlog",
  "active",
  "review",
  "done",
] as const;
export type StatusCategory = (typeof STATUS_CATEGORIES)[number];

export const STATUS_CATEGORY_LABELS: Record<StatusCategory, string> = {
  backlog: "To do",
  active: "In progress",
  review: "In review",
  done: "Done",
};

/** Archived sits outside every workflow: any task can be archived, whatever its status. */
export const ARCHIVED = "archived";

/** Where a task stands: its status category, or archived. */
export const TASK_STATES = [...STATUS_CATEGORIES, ARCHIVED] as const;
export type TaskState = (typeof TASK_STATES)[number];

export const TASK_STATE_LABELS: Record<TaskState, string> = {
  ...STATUS_CATEGORY_LABELS,
  archived: "Archived",
};

export function oneOf<T extends string>(values: readonly T[]) {
  return (value: unknown): value is T =>
    typeof value === "string" && (values as readonly string[]).includes(value);
}

export const isTaskState = oneOf(TASK_STATES);

/** The fixed statuses a subtask moves through, in place of a workflow. */
export const SUBTASK_CATEGORIES = [
  "backlog",
  "active",
  "done",
] as const satisfies readonly StatusCategory[];
export type SubtaskCategory = (typeof SUBTASK_CATEGORIES)[number];

const isSubtaskCategory = oneOf(SUBTASK_CATEGORIES);

/** Done or archived, so off the open lists either way. */
export function isTaskCompleted(
  task: Pick<AmpTaskRowFragment, "status" | "status_category" | "deleted_at">,
): boolean {
  return task.deleted_at !== null || taskCategory(task) === "done";
}

// Mirrors AMP_PROJECT_STATUSES.
export const PROJECT_STATUSES = ["draft", "in_progress", "done"] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: "Draft",
  in_progress: "In progress",
  done: "Done",
};

// A project's three statuses borrow the look of the task categories they match.
export const PROJECT_STATUS_CATEGORY: Record<ProjectStatus, StatusCategory> = {
  draft: "backlog",
  in_progress: "active",
  done: "done",
};

const isStatusCategory = oneOf(STATUS_CATEGORIES);

const isProjectStatus = oneOf(PROJECT_STATUSES);

export function toProjectStatus(value: string): ProjectStatus {
  return isProjectStatus(value) ? value : "draft";
}

export function statusCategory(
  status: Pick<AmpWorkflowStatusFragment, "category"> | null | undefined,
): StatusCategory {
  return status && isStatusCategory(status.category)
    ? status.category
    : "backlog";
}

/** Where a task stands, with archiving ahead of its status. */
export function taskState(
  task: Pick<AmpTaskRowFragment, "status" | "status_category" | "deleted_at">,
): TaskState {
  return task.deleted_at ? ARCHIVED : taskCategory(task);
}

/** A task's category: its workflow status's, or a subtask's own. */
export function taskCategory(task: {
  status: Pick<AmpWorkflowStatusFragment, "category"> | null | undefined;
  status_category: string | null;
}): StatusCategory {
  if (task.status) return statusCategory(task.status);
  return task.status_category && isStatusCategory(task.status_category)
    ? task.status_category
    : "backlog";
}

type OrderedStatus = Pick<AmpWorkflowStatusFragment, "category" | "position">;

/** Statuses in workflow order: by category, then by position inside it. */
export function sortStatuses<T extends OrderedStatus>(statuses: T[]): T[] {
  const rank = (status: T) => STATUS_CATEGORIES.indexOf(statusCategory(status));
  return [...statuses].sort(
    (a, b) => rank(a) - rank(b) || a.position - b.position,
  );
}

const WorkflowStatusSchema = z.object({
  id: z.string(),
  workflow_id: z.string(),
  name: z.string(),
  category: z.string(),
  color: z.string().nullable(),
  position: z.number(),
}) satisfies z.ZodType<AmpWorkflowStatusFragment>;

/** The status a new task starts in: this one, or the first of its project's workflow. */
export const TaskStartSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("status"), status: WorkflowStatusSchema }),
  z.object({ kind: z.literal("project-default") }),
]);
export type TaskStart = z.infer<typeof TaskStartSchema>;

/** A workflow's first backlog status, else its first, once its statuses have loaded. */
function firstStatusOf(
  statuses: AmpWorkflowStatusFragment[],
): AmpWorkflowStatusFragment | undefined {
  const ordered = sortStatuses(statuses);
  return ordered.find((status) => status.category === "backlog") ?? ordered[0];
}

/** The workflow a new task starts in; `defaultWorkflowId` is its project's. */
export function startWorkflowIdOf({
  start,
  defaultWorkflowId,
}: {
  start: TaskStart;
  defaultWorkflowId: string | null;
}): string | null {
  switch (start.kind) {
    case "status": {
      return start.status.workflow_id;
    }
    case "project-default": {
      return defaultWorkflowId;
    }
    default: {
      return start satisfies never;
    }
  }
}

/** The status a new task starts in, when the board has its workflow's statuses. */
export function startStatusOf({
  start,
  defaultWorkflowId,
  statusesFor,
}: {
  start: TaskStart;
  defaultWorkflowId: string | null;
  statusesFor: (workflowId: string | null) => AmpWorkflowStatusFragment[];
}): AmpWorkflowStatusFragment | undefined {
  switch (start.kind) {
    case "status": {
      return start.status;
    }
    case "project-default": {
      return firstStatusOf(statusesFor(defaultWorkflowId));
    }
    default: {
      return start satisfies never;
    }
  }
}

/** What the server needs to start a task where the board means it to. */
export function startInputOf(
  start: TaskStart,
): Pick<CreateAmpTaskInput, "statusId"> {
  switch (start.kind) {
    case "status": {
      return { statusId: start.status.id };
    }
    case "project-default": {
      return {};
    }
    default: {
      return start satisfies never;
    }
  }
}

type StatusPlacement<T> = {
  /** The statuses in their new order, each with the position it takes. */
  ordered: { status: T; position: number }[];
  /** Where a status added to the category lands. */
  newPosition: number;
};

/**
 * Renumbers a workflow so positions follow category order, which the board's
 * columns read. With `addTo`, leaves a slot at the end of that category.
 */
export function placeStatuses<T extends OrderedStatus>(
  statuses: T[],
  addTo?: StatusCategory,
): StatusPlacement<T> {
  const sorted = sortStatuses(statuses);
  const ordered: { status: T; position: number }[] = [];
  let next = 0;
  let newPosition = -1;
  for (const category of STATUS_CATEGORIES) {
    for (const status of sorted) {
      if (statusCategory(status) === category)
        ordered.push({ status, position: next++ });
    }
    if (category === addTo) newPosition = next++;
  }
  return { ordered, newPosition };
}

/**
 * A workflow after one status is dropped into a category at an index among
 * that category's other statuses, renumbered so positions follow the order.
 * Returns only the statuses whose position or category changes.
 */
export function moveStatus<T extends OrderedStatus & { id: string }>(
  statuses: T[],
  {
    statusId,
    category,
    index,
  }: {
    statusId: string;
    category: StatusCategory;
    index: number;
  },
): { status: T; position: number; category: StatusCategory }[] {
  const moving = statuses.find((status) => status.id === statusId);
  if (!moving) return [];
  const rest = sortStatuses(
    statuses.filter((status) => status.id !== statusId),
  );
  const ordered: { status: T; category: StatusCategory }[] = [];
  for (const candidate of STATUS_CATEGORIES) {
    const inCategory = rest.filter(
      (status) => statusCategory(status) === candidate,
    );
    if (candidate === category) {
      const at = Math.max(0, Math.min(index, inCategory.length));
      inCategory.splice(at, 0, moving);
    }
    for (const status of inCategory)
      ordered.push({ status, category: candidate });
  }
  return ordered.flatMap(({ status, category: placed }, position) =>
    status.position !== position || statusCategory(status) !== placed
      ? [{ status, position, category: placed }]
      : [],
  );
}

export function isOverdue(
  task: Pick<
    AmpTaskRowFragment,
    "due_at" | "status" | "status_category" | "deleted_at"
  >,
): boolean {
  if (!task.due_at || isTaskCompleted(task)) return false;
  return new Date(task.due_at).getTime() < Date.now();
}

/** Where "this week" ends for due dates: seven days from the start of today. */
export function endOfComingWeek(now: Date): Date {
  return addDays(startOfDay(now), 7);
}

function payloadValue(payload: unknown, key: string): unknown {
  if (typeof payload !== "object" || payload === null) return undefined;
  return (payload as Record<string, unknown>)[key];
}

export function payloadString(payload: unknown, key: string): string | null {
  const value = payloadValue(payload, key);
  return typeof value === "string" ? value : null;
}

// User ids are bigints, which event payloads hold as JSON numbers.
function payloadId(payload: unknown, key: string): string | null {
  const value = payloadValue(payload, key);
  if (typeof value === "number") return String(value);
  return typeof value === "string" ? value : null;
}

function payloadStrings(payload: unknown, key: string): string[] {
  const value = payloadValue(payload, key);
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

const TASK_FIELD_LABELS: Record<string, string> = {
  title: "title",
  description: "description",
  dueAt: "due date",
};

type EventContext = {
  statusName: (statusId: string) => string | null;
  userName: (userId: string) => string | null;
  groupName: (userGroupId: string) => string | null;
  tagName: (tagId: string) => string | null;
  /** Named "you" rather than by name. */
  currentUserId: string | undefined;
};

/** Who a task or project user event names: the user or group, by name when it's still known. */
function taskUserName(payload: unknown, context: EventContext): string {
  const userId = payloadId(payload, "userId");
  const userGroupId = payloadId(payload, "userGroupId");
  if (userId && userId === context.currentUserId) return "you";
  return (
    (userId && context.userName(userId)) ||
    (userGroupId && context.groupName(userGroupId)) ||
    "someone"
  );
}

/** A tag's name while it still exists; a deleted tag has none. */
function tagName(payload: unknown, context: EventContext): string {
  const tagId = payloadString(payload, "tagId");
  return (tagId && context.tagName(tagId)) || "";
}

/** What a task event calls the task: a subtask when it names a parent. */
function taskNoun(payload: unknown): string {
  return payloadString(payload, "parentTaskId")
    ? TERMS.subtask.singular
    : TERMS.task.singular;
}

/** One line of activity, written to follow the actor's name. */
export function describeEvent(
  event: ActivityEvent,
  context: EventContext,
): string {
  const { payload } = event;
  switch (event.kind) {
    case "opened":
      return `created the ${TERMS.project.singular}`;
    case "moved": {
      const to = payloadString(payload, "to");
      return to && isProjectStatus(to)
        ? `moved the ${TERMS.project.singular} to ${PROJECT_STATUS_LABELS[to]}`
        : `moved the ${TERMS.project.singular}`;
    }
    case "status_line":
      return `updated the status: ${payloadString(payload, "statusLine") ?? "cleared"}`;
    case "archived":
      return `archived the ${TERMS.project.singular}`;
    case "unarchived":
      return `restored the ${TERMS.project.singular}`;
    case "linked":
      return `attached ${linkedItemName(payload)}`;
    case "unlinked":
      return `removed ${linkedItemName(payload)}`;
    case "commented":
      return "commented";
    case "task_created":
      return `created the ${taskNoun(payload)}`;
    case "task_updated": {
      const fields = payloadStrings(payload, "fields").map(
        (field) => TASK_FIELD_LABELS[field] ?? field,
      );
      return fields.length > 0
        ? `changed the ${fields.join(", ")}`
        : `updated the ${TERMS.task.singular}`;
    }
    case "task_status_changed": {
      const name = taskStatusName(payload, context);
      if (!name) return "changed the status";
      const isSubtask = isSubtaskCategory(
        payloadString(payload, "toCategory") ?? "",
      );
      return `moved the ${isSubtask ? TERMS.subtask.singular : TERMS.task.singular} to ${name}`;
    }
    case "task_user_added": {
      const name = taskUserName(payload, context);
      const type = payloadString(payload, "type");
      if (type === "reviewer") return `added ${name} as a reviewer`;
      if (type === "assignee") return `assigned ${name}`;
      return `made ${name} an owner`;
    }
    case "task_user_removed": {
      const name = taskUserName(payload, context);
      const type = payloadString(payload, "type");
      if (type === "reviewer") return `removed ${name} as a reviewer`;
      if (type === "assignee") return `unassigned ${name}`;
      return `removed ${name} as an owner`;
    }
    case "user_added": {
      const name = taskUserName(payload, context);
      return payloadString(payload, "type") === "owner"
        ? `made ${name} an owner`
        : `added ${name} to the ${TERMS.project.singular}`;
    }
    case "user_removed": {
      const name = taskUserName(payload, context);
      return payloadString(payload, "type") === "owner"
        ? `removed ${name} as an owner`
        : `removed ${name} from the ${TERMS.project.singular}`;
    }
    case "task_archived":
      return `archived the ${TERMS.task.singular}`;
    case "task_unarchived":
      return `restored the ${TERMS.task.singular}`;
    case "task_tagged":
      return `added the tag ${tagName(payload, context)}`.trim();
    case "task_untagged":
      return `removed the tag ${tagName(payload, context)}`.trim();
    case "task_blocker_added": {
      const title = payloadString(payload, "blockerTitle");
      return title ? `marked it blocked by ${title}` : "added a blocker";
    }
    case "task_blocker_removed": {
      const title = payloadString(payload, "blockerTitle");
      return title ? `removed the blocker ${title}` : "removed a blocker";
    }
    case "action_started":
      return "started an action";
    case "task_review_approved":
      return `approved the ${taskNoun(payload)}`;
    case "task_review_changes_requested":
      return "requested changes";
    case "task_review_commented":
      return "commented in a review";
    case "task_review_requested":
      return "requested a review";
    case "canvas_file_added":
      return `added ${canvasFileName(payload)} to the canvas`;
    case "canvas_file_removed":
      return `removed ${canvasFileName(payload)} from the canvas`;
    case "task_review_withdrawn":
      return "withdrew their review";
    case "file_renamed": {
      const to = payloadString(payload, "to");
      return to ? `renamed the file to ${to}` : "renamed the file";
    }
    case "file_edited":
      return "edited the file";
    case "file_version_restored":
      return "restored an earlier version";
    case "file_deleted":
      return "deleted the file";
    case "file_restored":
      return "restored the file";
  }
}

/** The status a task moved to: a subtask's category, else its workflow status. */
function taskStatusName(
  payload: unknown,
  context: EventContext,
): string | null {
  const toCategory = payloadString(payload, "toCategory");
  if (toCategory && isSubtaskCategory(toCategory)) {
    return STATUS_CATEGORY_LABELS[toCategory];
  }
  const toId = payloadString(payload, "toStatusId");
  return toId ? context.statusName(toId) : null;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A name standing as an object: a fallback like "an audience" reads as "Audience", and a real name stays as written. */
function glyphName(name: string): string {
  const noun = name.match(/^an? (.+)$/)?.[1];
  return noun ? capitalize(noun) : name;
}

/** What adding or removing a task user did, by their role: the verb before the person, and the words after. */
const TASK_USER_PHRASES: Record<
  string,
  Record<"added" | "removed", [verb: string, suffix: string | null]>
> = {
  reviewer: {
    added: ["Asked for review from", null],
    removed: ["Removed", "as reviewer"],
  },
  assignee: { added: ["Assigned", null], removed: ["Unassigned", null] },
  owner: { added: ["Made", "owner"], removed: ["Removed", "as owner"] },
};

/** An event as a short phrase: an icon for its kind, a verb, and the name of what it acted on. */
export type EventGlyph = {
  icon: typeof PaperclipIcon;
  /** "Attached", "Moved to": capitalized, since it starts the phrase. */
  verb: string;
  /** The file attached, the status moved to. Null when the verb says it all. */
  object: string | null;
  /** Words after the object: "owner" in "Made you owner". */
  suffix: string | null;
  /** Where the object opens, when it names a Library file. */
  href: string | null;
};

/**
 * An event as an icon, a verb and an object, like "Attached Brief" by a paperclip.
 * Null for a comment, whose phrase depends on why the viewer got it.
 */
export function eventGlyph(
  event: ActivityEvent,
  context: EventContext,
): EventGlyph | null {
  const { payload } = event;
  const glyph = (
    icon: EventGlyph["icon"],
    verb: string,
    object: string | null = null,
    { href = null, suffix = null }: Partial<EventGlyph> = {},
  ): EventGlyph => ({ icon, verb, object, href, suffix });
  switch (event.kind) {
    case "linked":
    case "unlinked": {
      const fileId = payloadString(payload, "id");
      return glyph(
        PaperclipIcon,
        event.kind === "linked" ? "Attached" : "Removed",
        glyphName(linkedItemName(payload)),
        {
          href:
            payloadString(payload, "kind") === "file" && fileId
              ? libraryFilePath({
                  id: fileId,
                  canvasId: payloadString(payload, "canvasId"),
                })
              : null,
        },
      );
    }
    case "canvas_file_added":
    case "canvas_file_removed": {
      const fileId = payloadString(payload, "fileId");
      const isAdded = event.kind === "canvas_file_added";
      return glyph(
        CanvasIcon,
        isAdded ? "Added" : "Removed",
        glyphName(canvasFileName(payload)),
        {
          href: fileId
            ? libraryFilePath({
                id: fileId,
                canvasId: payloadString(payload, "canvasId"),
              })
            : null,
          suffix: isAdded ? "to the canvas" : "from the canvas",
        },
      );
    }
    case "moved": {
      const to = payloadString(payload, "to");
      return to && isProjectStatus(to)
        ? glyph(ArrowRightCircleIcon, "Moved to", PROJECT_STATUS_LABELS[to])
        : null;
    }
    case "task_status_changed": {
      const name = taskStatusName(payload, context);
      return name ? glyph(ArrowRightCircleIcon, "Moved to", name) : null;
    }
    case "task_user_added":
    case "task_user_removed": {
      const isAdded = event.kind === "task_user_added";
      const [verb, suffix] = TASK_USER_PHRASES[
        payloadString(payload, "type") ?? ""
      ]?.[isAdded ? "added" : "removed"] ?? [
        isAdded ? "Added" : "Removed",
        null,
      ];
      return glyph(UserIcon, verb, taskUserName(payload, context), { suffix });
    }
    case "user_added":
    case "user_removed":
      return glyph(
        UserIcon,
        event.kind === "user_added" ? "Added" : "Removed",
        taskUserName(payload, context),
      );
    case "task_tagged":
    case "task_untagged":
      return glyph(
        TagIcon,
        event.kind === "task_tagged" ? "Tagged" : "Untagged",
        tagName(payload, context) || null,
      );
    case "task_blocker_added":
    case "task_blocker_removed":
      return glyph(
        LinkIcon,
        event.kind === "task_blocker_added" ? "Blocked by" : "Unblocked from",
        payloadString(payload, "blockerTitle"),
      );
    case "task_updated": {
      const fields = payloadStrings(payload, "fields").map(
        (field) => TASK_FIELD_LABELS[field] ?? field,
      );
      return glyph(EditIcon, "Edited", fields.join(", ") || null);
    }
    case "status_line":
      return glyph(
        EditIcon,
        "Updated the status",
        payloadString(payload, "statusLine"),
      );
    case "opened":
    case "task_created":
      return glyph(PlusIcon, "Created");
    case "archived":
    case "task_archived":
      return glyph(ArchiveIcon, "Archived");
    case "unarchived":
    case "task_unarchived":
      return glyph(ArchiveIcon, "Restored");
    case "action_started":
      return glyph(
        LightningBoltIcon,
        "Started",
        payloadString(payload, "title"),
      );
    case "task_review_approved":
      return glyph(CheckIcon, "Approved");
    case "task_review_changes_requested":
      return glyph(EditIcon, "Requested changes");
    case "task_review_commented":
      return glyph(CommentIcon, "Commented in a review");
    case "task_review_requested":
      return glyph(
        EyeIcon,
        "Asked for review from",
        taskUserName(payload, context),
      );
    case "task_review_withdrawn":
      return glyph(EyeIcon, "Withdrew their review");
    case "file_renamed":
      return glyph(EditIcon, "Renamed to", payloadString(payload, "to"));
    case "file_edited":
      return glyph(EditIcon, "Edited");
    case "file_version_restored":
      return glyph(HistoryIcon, "Restored an earlier version");
    case "file_deleted":
      return glyph(DeleteIcon, "Deleted");
    case "file_restored":
      return glyph(ArchiveIcon, "Restored");
    case "commented":
      return null;
  }
}

export const CATEGORY_COLORS: Record<TaskState, string> = {
  backlog: "icon.tertiary",
  active: "blue.base",
  review: "warning.base",
  done: "icon.success",
  archived: "gray.400",
};

const BOARD_COLOR_LABELS: Record<AmpBoardColorName, string> = {
  gray: "Gray",
  blue: "Blue",
  green: "Green",
  yellow: "Yellow",
  orange: "Orange",
  red: "Red",
  purple: "Purple",
  pink: "Pink",
};

/** The colors a status or tag can take. The value is stored as the token name. */
export const BOARD_COLOR_OPTIONS = AMP_BOARD_COLOR_NAMES.map((name) => ({
  label: BOARD_COLOR_LABELS[name],
  value: AMP_BOARD_COLORS[name],
}));

const BOARD_COLOR_VALUES = new Set<string>(
  BOARD_COLOR_OPTIONS.map((option) => option.value),
);

/**
 * Status and tag colors are free text, and agents write them too, so only a
 * known token or a hex color renders; anything else takes the fallback.
 */
export function resolveBoardColor(
  color: string | null | undefined,
  fallback: string,
): string {
  if (!color) return fallback;
  if (BOARD_COLOR_VALUES.has(color)) return color;
  return /^#[0-9a-f]{3,8}$/i.test(color) ? color : fallback;
}

export function statusColor(
  status: Pick<AmpWorkflowStatusFragment, "category" | "color"> | null,
): string {
  const category = statusCategory(status);
  return resolveBoardColor(status?.color, CATEGORY_COLORS[category]);
}

// A start date means the start of that day where the person picked it.
export function startOfDayIso(date: Date): string {
  return startOfDay(date).toISOString();
}

// Stored due dates end at 23:59:59.000, so new ones drop the milliseconds too.
export function endOfDayIso(date: Date): string {
  return setMilliseconds(endOfDay(date), 0).toISOString();
}

// The link target kinds the board writes into linked and unlinked events.
// canvas and email are what Ad Studio projects and emails were linked as
// before they attached as Library files.
const LINK_EVENT_NAMES = new Map([
  ["chat_session", "a chat"],
  ["audience", "an audience"],
  ["canvas", "an Ad Studio project"],
  ["brief_version", "a brief"],
  ["email", "an email"],
  ["action", "an agent action"],
  ["opportunity", "an opportunity"],
  ["sync", "a sync"],
  ["campaign", "a campaign"],
  ["campaign_artifact", "a campaign artifact"],
  ["action_plan", "an action plan"],
  ["file", "a file"],
  ["external", "a link"],
]);

// A Library file's type, which a file link's or canvas node's event carries.
const FILE_EVENT_NAMES = new Map([
  ["ad", "an ad"],
  ["email", "an email"],
  ["sms", "an SMS"],
  ["push", "a push notification"],
  ["html", "a web page"],
  ["doc", "a document"],
  ["canvas", "a canvas"],
  ["slides", "a presentation"],
  ["upload", "a file"],
]);

/** The file a canvas event names: by name while it has one, else by its type. */
function canvasFileName(payload: unknown): string {
  const fileName = payloadString(payload, "fileName");
  const fileType = payloadString(payload, "fileType");
  return fileName || (fileType && FILE_EVENT_NAMES.get(fileType)) || "a file";
}

function linkedItemName(payload: unknown): string {
  const label =
    payloadString(payload, "label") ?? payloadString(payload, "fileName");
  if (label) return label;
  const fileType = payloadString(payload, "fileType");
  const kind = payloadString(payload, "kind");
  return (
    (fileType && FILE_EVENT_NAMES.get(fileType)) ??
    (kind && LINK_EVENT_NAMES.get(kind)) ??
    "an item"
  );
}

type ProjectTaskCounts = Pick<
  AmpProjectRowFragment,
  | "tasks_aggregate"
  | "backlog_tasks"
  | "active_tasks"
  | "review_tasks"
  | "done_tasks"
>;

/** The workspace's default workflow, or its first when none is marked. */
export function defaultWorkflowId(
  workflows: { id: string; is_default: boolean }[],
): string {
  return (
    workflows.find((workflow) => workflow.is_default)?.id ??
    workflows[0]?.id ??
    ""
  );
}

/** A project takes tasks only once it has a task board, which is a workflow. */
export function hasTaskBoard<T extends { workflow_id: string | null }>(
  project: T,
): project is T & { workflow_id: string } {
  return project.workflow_id !== null;
}

/** How many pieces of work, chats aside, are linked to the project itself. */
export function projectWorkCount(project: {
  work_aggregate: { aggregate: { count: number } | null };
}): number {
  return project.work_aggregate.aggregate?.count ?? 0;
}

/** How many of a project's tasks sit in each status category, and in all. */
export function projectProgress(project: ProjectTaskCounts): {
  total: number;
  completed: number;
  byCategory: Record<StatusCategory, number>;
} {
  const byCategory: Record<StatusCategory, number> = {
    backlog: project.backlog_tasks.aggregate?.count ?? 0,
    active: project.active_tasks.aggregate?.count ?? 0,
    review: project.review_tasks.aggregate?.count ?? 0,
    done: project.done_tasks.aggregate?.count ?? 0,
  };
  return {
    total: project.tasks_aggregate.aggregate?.count ?? 0,
    completed: byCategory.done,
    byCategory,
  };
}

export const PROJECT_HEALTHS = ["on_track", "at_risk"] as const;
export type ProjectHealth = (typeof PROJECT_HEALTHS)[number];

export const PROJECT_HEALTH_LABELS: Record<ProjectHealth, string> = {
  on_track: "On track",
  at_risk: "At risk",
};

/**
 * How a project under way is doing against its due date, or null when it
 * isn't under way. It's at risk once overdue, or when due within the week
 * with less than half its tasks done.
 */
export function projectHealth(
  project: Pick<AmpProjectRowFragment, "status" | "due_at"> & ProjectTaskCounts,
  now: Date = new Date(),
): ProjectHealth | null {
  if (toProjectStatus(project.status) !== "in_progress") return null;
  if (!project.due_at) return "on_track";
  const due = new Date(project.due_at).getTime();
  if (due < now.getTime()) return "at_risk";
  const { total, completed } = projectProgress(project);
  const isDueThisWeek = due < endOfComingWeek(now).getTime();
  return isDueThisWeek && completed * 2 < total ? "at_risk" : "on_track";
}

/** When a project is due, as "Due Oct 14", and whether it's past that while still open. */
export function projectDueLabel(
  project: Pick<AmpProjectRowFragment, "status" | "due_at">,
  now: Date = new Date(),
): { label: string; isOverdue: boolean } | null {
  if (!project.due_at) return null;
  const due = new Date(project.due_at);
  return {
    label: `Due ${format(due, "MMM d")}`,
    isOverdue:
      toProjectStatus(project.status) !== "done" &&
      due.getTime() < now.getTime(),
  };
}

/** A project's due date with how far off it is while open, as "Due Oct 14 · in 13 days" or "Due Oct 1 · 2 days overdue". */
export function projectDueSummary(
  project: Pick<AmpProjectRowFragment, "status" | "due_at">,
  now: Date = new Date(),
): { label: string; isOverdue: boolean } | null {
  const due = projectDueLabel(project, now);
  if (!due || !project.due_at || toProjectStatus(project.status) === "done") {
    return due;
  }
  const days = differenceInCalendarDays(new Date(project.due_at), now);
  return { ...due, label: `${due.label} · ${relativeDays(days)}` };
}

/** A task's due date with how far off it is while open, as "Due Oct 14 · in 13 days". */
export function taskDueSummary(
  task: Pick<
    AmpTaskRowFragment,
    "due_at" | "status" | "status_category" | "deleted_at"
  >,
  now: Date = new Date(),
): { label: string; isOverdue: boolean } | null {
  if (!task.due_at) return null;
  const due = new Date(task.due_at);
  const label = `Due ${format(due, "MMM d")}`;
  if (isTaskCompleted(task)) return { label, isOverdue: false };
  const days = differenceInCalendarDays(due, now);
  return {
    label: `${label} · ${relativeDays(days)}`,
    isOverdue: due.getTime() < now.getTime(),
  };
}

function relativeDays(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days > 1) return `in ${days} days`;
  return days === -1 ? "1 day overdue" : `${-days} days overdue`;
}

/** When a project last changed, as "Updated 2h ago" or "Updated Oct 3". */
export function projectUpdatedLabel(updatedAt: string): string {
  const ago = formatRelativeShort(updatedAt);
  // formatRelativeShort capitalizes "Just now" to open a sentence; here it follows "Updated".
  return `Updated ${ago === "Just now" ? "just now" : ago}`;
}

export const PROJECT_EXTERNAL_REF_LABELS: Record<
  AmpProjectExternalRefType,
  string
> = {
  asana: "Asana",
  workfront: "Workfront",
};

const isExternalRefType = oneOf(AMP_PROJECT_EXTERNAL_REF_TYPES);

/** The tool a project was synced from, read from its loosely typed jsonb column. */
export function projectExternalRef(
  value: unknown,
): AmpProjectExternalRef | null {
  const type = payloadValue(value, "type");
  const id = payloadString(value, "id");
  const url = payloadString(value, "url");
  if (!isExternalRefType(type) || !id) return null;
  return url ? { type, id, url } : { type, id };
}

/** Everyone on a project, owners first. */
export function projectPeople(
  project: Pick<AmpProjectRowFragment, "owners" | "members">,
) {
  return [...project.owners, ...project.members].flatMap((person) =>
    person.user ? [person.user] : [],
  );
}

/** The tasks a task blocks, directly or through a chain, which can't block it in turn. */
export function tasksBlockedBy(
  taskId: string,
  relations: { source_task_id: string; target_task_id: string }[],
): Set<string> {
  const blocked = new Set<string>();
  const queue = [taskId];
  for (let current = queue.pop(); current; current = queue.pop()) {
    for (const relation of relations) {
      if (
        relation.source_task_id === current &&
        !blocked.has(relation.target_task_id)
      ) {
        blocked.add(relation.target_task_id);
        queue.push(relation.target_task_id);
      }
    }
  }
  return blocked;
}

/** The brand profile a new project starts with: the workspace default, or its first profile when none is marked. */
export function defaultBrandProfileId(
  brandProfiles: { id: string; is_default: boolean }[],
): string | null {
  return (
    (brandProfiles.find((profile) => profile.is_default) ?? brandProfiles[0])
      ?.id ?? null
  );
}

/** A task's reviewers, users and groups both, keyed apart since a user and a group can share an id. */
export function reviewerPeople(task: Pick<AmpTaskRowFragment, "reviewers">) {
  return task.reviewers.flatMap((reviewer) => {
    if (reviewer.user) {
      return [{ id: `user:${reviewer.user.id}`, name: reviewer.user.name }];
    }
    if (reviewer.user_group) {
      return [
        {
          id: `group:${reviewer.user_group.id}`,
          name: reviewer.user_group.name,
          isGroup: true,
        },
      ];
    }
    return [];
  });
}

/** The user and group ids in a `MemberMultiSelect` value that allows groups. */
export function splitPersonKeys(keys: string[]) {
  return {
    userIds: keys.flatMap((key) =>
      key.startsWith("user:") ? [key.slice("user:".length)] : [],
    ),
    userGroupIds: keys.flatMap((key) =>
      key.startsWith("group:") ? [key.slice("group:".length)] : [],
    ),
  };
}
